import React, { useState, useEffect } from "react";
import { Modal, Card, Typography, Divider, Space, Tag, Button, Spin, Alert, message } from "antd";
import { MailOutlined, UserOutlined, CopyOutlined, CloseOutlined, DownloadOutlined, EyeOutlined, TeamOutlined } from "@ant-design/icons";
import BulkEmailRecipientsViewer from "./BulkEmailRecipientsViewer";
import { authFetch } from "../../api/authFetch";
import { getEmailFullStatus } from "../../api/mailApi";
import dayjs from "dayjs";

const { Title, Text } = Typography;

/**
 * 通用邮件查看组件
 * 用于显示已发送的邮件内容，支持模态框和内嵌两种模式
 */
const EmailViewer = ({
    visible = false,
    onClose,
    emailId = null,
    emailData = null, // 可以直接传入邮件数据，避免重复请求
    title = "邮件详情",
    width = 800,
    mode = "modal", // "modal" | "inline"
    showActions = true,
    onEmailAction = null, // 邮件操作回调（如转发、回复等）
}) => {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState(null);
    const [error, setError] = useState(null);

    // 群发邮件收件人状态查看器状态
    const [recipientsViewerVisible, setRecipientsViewerVisible] = useState(false);

    // 加载邮件详情
    const loadEmailDetail = async () => {
        if (!emailId && !emailData) {
            setError("缺少邮件ID或邮件数据");
            return;
        }

        // 如果直接传入了邮件数据，使用它
        if (emailData) {
            // 对于群发邮件，通过API获取并增强其数据，以获得最新的详细状态
            if (emailData.is_bulk_email && emailData.related_email_id) {
                try {
                    setLoading(true);
                    // 使用统一的邮件API获取完整状态信息，符合重构设计原则
                    const statusResponse = await getEmailFullStatus(emailData.related_email_id, true);

                    let enhancedEmailData = { ...emailData };

                    // 如果API调用成功并返回了有效数据
                    if (statusResponse?.data) {
                        const statusData = statusResponse.data;

                        // 更新基本邮件信息
                        enhancedEmailData = {
                            ...enhancedEmailData,
                            subject: statusData.subject || enhancedEmailData.subject,
                            from_address: statusData.from_address,
                            to_address: statusData.to_address,
                            cc_address: statusData.cc_address,
                            send_time: statusData.sent_at || statusData.date || enhancedEmailData.send_time,
                            status: statusData.status || enhancedEmailData.status,
                            content_html: statusData.body_html || enhancedEmailData.content_html,
                            rendered_html: statusData.body_html || enhancedEmailData.rendered_html,
                            content: statusData.body_text || enhancedEmailData.content,
                        };

                        // 处理群发邮件的详细状态
                        if (statusData.bulk_status && statusData.bulk_status.summary) {
                            const bulkSummary = statusData.bulk_status.summary;

                            // 更新群发邮件统计信息
                            enhancedEmailData.bulk_email_stats = {
                                total_recipients: bulkSummary.total_recipients || 0,
                                success_count: bulkSummary.total_sent || bulkSummary.sent_count || 0,
                                failed_count: bulkSummary.total_failed || bulkSummary.failed_count || 0,
                                pending_count: bulkSummary.total_pending || 0,
                                overall_status: bulkSummary.overall_status || "UNKNOWN",
                            };

                            // 如果有收件人详细信息，更新收件人列表
                            if (statusData.bulk_status.recipients && Array.isArray(statusData.bulk_status.recipients)) {
                                const allRecipients = statusData.bulk_status.recipients;

                                // 根据收件人类型分组
                                const toRecipients = allRecipients.filter((r) => r.type === "TO").map((r) => r.email);
                                const ccRecipients = allRecipients.filter((r) => r.type === "CC").map((r) => r.email);
                                const bccRecipients = allRecipients.filter((r) => r.type === "BCC").map((r) => r.email);

                                enhancedEmailData = {
                                    ...enhancedEmailData,
                                    recipients: toRecipients,
                                    to: toRecipients,
                                    cc: ccRecipients,
                                    bcc: bccRecipients,
                                };
                            } else {
                                // 如果没有详细收件人信息，使用基本的收件人地址
                                if (statusData.to_address) {
                                    const toList = statusData.to_address
                                        .split(/[,;]/)
                                        .map((email) => email.trim())
                                        .filter(Boolean);
                                    enhancedEmailData.recipients = toList;
                                    enhancedEmailData.to = toList;
                                }
                                if (statusData.cc_address) {
                                    const ccList = statusData.cc_address
                                        .split(/[,;]/)
                                        .map((email) => email.trim())
                                        .filter(Boolean);
                                    enhancedEmailData.cc = ccList;
                                }
                            }
                        }
                    }

                    setEmail(enhancedEmailData);
                } catch (err) {
                    console.error("获取群发邮件详细信息失败:", err);
                    // 如果获取增强信息失败，仍然显示传入的基础邮件数据
                    setEmail(emailData);
                } finally {
                    setLoading(false);
                }
            } else {
                // 对于非群发邮件，直接使用传入的数据
                setEmail(emailData);
            }
            return;
        }

        // --- 以下为通过 emailId 加载邮件的备用逻辑 ---
        try {
            setLoading(true);
            setError(null);

            // TODO: 此处应替换为实际的邮件详情API调用
            const response = await authFetch(`/api/v1/emails/${emailId}`, {}, { entrypoint: "email-viewer-detail" });
            if (!response.ok) {
                throw new Error(`获取邮件失败: ${response.statusText}`);
            }

            const emailDetail = await response.json();
            setEmail(emailDetail);
        } catch (err) {
            console.error("加载邮件详情失败:", err);
            setError(`加载邮件详情失败: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // 当visible或emailId变化时加载数据
    useEffect(() => {
        if ((visible || mode === "inline") && (emailId || emailData)) {
            loadEmailDetail();
        }
    }, [visible, emailId, emailData, mode]);

    // 解析收件人列表
    const parseRecipients = (recipientData) => {
        if (!recipientData) return [];

        if (Array.isArray(recipientData)) {
            return recipientData.filter(Boolean);
        }

        if (typeof recipientData === "string") {
            return recipientData
                .split(/[,;]/)
                .map((email) => email.trim())
                .filter(Boolean);
        }

        return [];
    };

    // 渲染邮件内容
    const renderEmailContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>加载邮件内容中...</div>
                </div>
            );
        }

        if (error) {
            return <Alert message="加载失败" description={error} type="error" showIcon style={{ margin: "20px 0" }} />;
        }

        if (!email) {
            return <Alert message="暂无邮件数据" type="info" showIcon style={{ margin: "20px 0" }} />;
        }

        const toList = parseRecipients(email.recipients || email.to);
        const ccList = parseRecipients(email.cc);
        const bccList = parseRecipients(email.bcc);
        const attachments = email.attachments || [];

        return (
            <Card bordered={false} style={{ margin: 0 }}>
                {/* 邮件头部信息 */}
                <div style={{ marginBottom: 24 }}>
                    {/* 邮件主题 */}
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ color: "#666", fontSize: "12px" }}>
                            主题
                        </Text>
                        <Title level={4} style={{ margin: "4px 0 0 0", color: "#333" }}>
                            {email.subject || email.email_subject || "无主题"}
                        </Title>
                    </div>

                    {/* 发送时间和状态 */}
                    <div style={{ marginBottom: 16 }}>
                        <Space>
                            <Tag icon={<MailOutlined />} color="blue">
                                {email.email_type || "邮件"}
                            </Tag>
                            {email.send_time && <Text type="secondary">发送时间: {dayjs(email.send_time).format("YYYY-MM-DD HH:mm:ss")}</Text>}
                            {email.status && <Tag color={email.status === "sent" ? "green" : email.status === "failed" ? "red" : email.status === "sending" ? "processing" : "default"}>{email.status === "sent" ? "已发送" : email.status === "failed" ? "发送失败" : email.status === "sending" ? "发送中" : email.status}</Tag>}
                            {email.is_bulk_email && <Tag color="purple">群发邮件</Tag>}
                        </Space>
                    </div>

                    {/* 群发邮件统计信息 */}
                    {email.is_bulk_email && email.bulk_email_stats && (
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ color: "#666", fontSize: "12px", display: "block", marginBottom: 4 }}>
                                发送统计
                            </Text>
                            <Space>
                                <Tag color="blue">总数: {email.bulk_email_stats.total_recipients || 0}</Tag>
                                <Tag color="green">成功: {email.bulk_email_stats.success_count || 0}</Tag>
                                <Tag color="red">失败: {email.bulk_email_stats.failed_count || 0}</Tag>
                                {email.bulk_email_stats.overall_status && <Tag color={email.bulk_email_stats.overall_status === "ALL_SENT" ? "green" : email.bulk_email_stats.overall_status === "PARTIAL_SENT" ? "orange" : email.bulk_email_stats.overall_status === "ALL_FAILED" ? "red" : "default"}>{email.bulk_email_stats.overall_status}</Tag>}
                            </Space>
                            {/* 查看详细收件人状态按钮 */}
                            {email.related_email_id && (
                                <div style={{ marginTop: 8 }}>
                                    <Button size="small" icon={<TeamOutlined />} onClick={() => setRecipientsViewerVisible(true)}>
                                        查看详细收件人状态
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    <Divider style={{ margin: "16px 0" }} />

                    {/* 收件人信息 */}
                    <div style={{ marginBottom: 16 }}>
                        {toList.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: "#666", fontSize: "12px", display: "block", marginBottom: 4 }}>
                                    收件人
                                </Text>
                                <Space wrap>
                                    {toList.map((email, index) => (
                                        <Tag key={index} icon={<UserOutlined />} color="blue">
                                            {email}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        )}

                        {ccList.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: "#666", fontSize: "12px", display: "block", marginBottom: 4 }}>
                                    抄送
                                </Text>
                                <Space wrap>
                                    {ccList.map((email, index) => (
                                        <Tag key={index} icon={<CopyOutlined />} color="cyan">
                                            {email}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        )}

                        {bccList.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: "#666", fontSize: "12px", display: "block", marginBottom: 4 }}>
                                    密送
                                </Text>
                                <Space wrap>
                                    {bccList.map((email, index) => (
                                        <Tag key={index} icon={<CopyOutlined />} color="purple">
                                            {email}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        )}

                        {attachments.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: "#666", fontSize: "12px", display: "block", marginBottom: 4 }}>
                                    附件 ({attachments.length})
                                </Text>
                                <Space wrap>
                                    {attachments.map((attachment, index) => (
                                        <Tag
                                            key={index}
                                            icon={<DownloadOutlined />}
                                            color="orange"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                // TODO: 实现附件下载功能
                                                message.info("附件下载功能开发中...");
                                            }}
                                        >
                                            {attachment.filename || attachment.name || `附件 ${index + 1}`}
                                            {attachment.file_size && <span style={{ marginLeft: 4, fontSize: "10px", opacity: 0.7 }}>({(attachment.file_size / 1024).toFixed(1)}KB)</span>}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                    </div>

                    <Divider style={{ margin: "16px 0" }} />
                </div>

                {/* 邮件正文内容 */}
                <div>
                    <Text strong style={{ color: "#666", fontSize: "12px", display: "block", marginBottom: 8 }}>
                        邮件内容
                    </Text>
                    <div
                        style={{
                            minHeight: "200px",
                            padding: "16px",
                            backgroundColor: "#fafafa",
                            borderRadius: "6px",
                            border: "1px solid #f0f0f0",
                            fontSize: "14px",
                            lineHeight: "1.6",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            overflowY: "auto",
                        }}
                        dangerouslySetInnerHTML={{
                            __html: email.content_html || email.rendered_html || email.content || "暂无邮件内容",
                        }}
                    />
                </div>

                {/* 操作按钮 */}
                {showActions && onEmailAction && (
                    <div style={{ marginTop: 24, textAlign: "right" }}>
                        <Space>
                            <Button icon={<EyeOutlined />} onClick={() => onEmailAction("preview", email)}>
                                预览
                            </Button>
                            <Button type="primary" onClick={() => onEmailAction("forward", email)}>
                                转发
                            </Button>
                        </Space>
                    </div>
                )}
            </Card>
        );
    };

    // 模态框模式
    if (mode === "modal") {
        return (
            <>
                <Modal title={title} open={visible} onCancel={onClose} footer={null} width={width} destroyOnHidden style={{ top: 20 }} closeIcon={<CloseOutlined />}>
                    {renderEmailContent()}
                </Modal>

                {/* 群发邮件收件人状态查看器 */}
                {email?.is_bulk_email && email?.related_email_id && <BulkEmailRecipientsViewer visible={recipientsViewerVisible} onClose={() => setRecipientsViewerVisible(false)} mainEmailId={email.related_email_id} title={`收件人状态 - ${email.subject || email.email_subject || "无主题"}`} />}
            </>
        );
    }

    // 内嵌模式
    return (
        <>
            <div style={{ width: "100%" }}>{renderEmailContent()}</div>

            {/* 群发邮件收件人状态查看器 */}
            {email?.is_bulk_email && email?.related_email_id && <BulkEmailRecipientsViewer visible={recipientsViewerVisible} onClose={() => setRecipientsViewerVisible(false)} mainEmailId={email.related_email_id} title={`收件人状态 - ${email.subject || email.email_subject || "无主题"}`} />}
        </>
    );
};

export default EmailViewer;
